import React, { useContext, useState } from 'react';

import { errorMessages } from 'src/translates/errorMessages';

import { Button, ButtonVariant, DefaultErrorOverlay, Text, ZIndex } from '@ael/shared-components-library/student-shell';
import classNames from 'classnames';
import { IntlContext, MessageDescriptor } from 'react-intl';

type ErrorOverlayProps = {
  error: Error;
  resetErrorBoundary: () => void;
};

// This is the last-resort error boundary fallback and may render outside the IntlProvider.
// Unlike useIntl(), reading IntlContext directly does not throw when the provider is missing,
// so we use translations when available and fall back to plain English otherwise.
const useSafeFormatMessage = () => {
  const intl = useContext(IntlContext);

  return (descriptor: MessageDescriptor, englishFallback: string, values?: Record<string, string>) => {
    if (!intl) {
      return englishFallback;
    }

    try {
      return intl.formatMessage(descriptor, values);
    } catch {
      return englishFallback;
    }
  };
};

export const UnhandledError = (props: ErrorOverlayProps) => {
  const formatMessage = useSafeFormatMessage();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <ZIndex index={9999}>
      <DefaultErrorOverlay
        title={formatMessage(errorMessages.somethingWentWrongTitle, 'Something went wrong')}
        description={
          <>
            <p>
              {formatMessage(
                errorMessages.serverErrorDescription,
                "It's not you, it's us! We have detected an error when communicating with server."
              )}
            </p>
            <p>{formatMessage(errorMessages.reloadHint, 'Reload the page to see, if the problem persists.')}</p>
          </>
        }
      >
        <div className={classNames('flex gap-3')}>
          <Button variant={ButtonVariant.DANGER} onClick={props.resetErrorBoundary}>
            {/*type to be changed, after Marios feature*/}
            {formatMessage(errorMessages.reload, 'Reload')}
          </Button>

          <Button variant={ButtonVariant.BLUE} onClick={() => setShowDetails(!showDetails)}>
            {showDetails
              ? formatMessage(errorMessages.hideDetails, 'Hide details')
              : formatMessage(errorMessages.showDetails, 'Show details')}
          </Button>
        </div>

        {showDetails && <UnhandledErrorDetails error={props.error} />}
      </DefaultErrorOverlay>
    </ZIndex>
  );
};

const UnhandledErrorDetails = (props: { error: Error }) => {
  const formatMessage = useSafeFormatMessage();

  return (
    <>
      <Text>
        {formatMessage(errorMessages.errorName, `Error name: ${props.error.name}`, { name: props.error.name })}
      </Text>
      <Text>
        {formatMessage(errorMessages.errorMessage, `Error message: ${props.error.message}`, {
          message: props.error.message,
        })}
      </Text>
      <Text>{formatMessage(errorMessages.errorStackTrace, 'Error stack trace:')}</Text>
      <Text>{props.error.stack}</Text>
    </>
  );
};
